import { VNode } from 'preact';

import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { classNames } from '../utils/UNSAFE_classNames';
import { ratios, variant } from './AspectRatioStyles.css';

// AspectRatio uses the common padding-bottom as a percentage
// hack to get aspect-ratio to work.
// CSS now has an aspect-ratio attribute that makes this super easy.
// aspect-ratio is supported in all major browsers, most recently in Safari 15.
// https://developer.mozilla.org/en-US/docs/Web/CSS/aspect-ratio
// However, it isn't until jet 14 that we drop support for Safari 14.
// When jet 14 comes out we can replace the padding-bottom hack with aspect-ratio css property.
// Also a user could just put aspect-ratio directly on their dom, instead of wrapping
// it inside this AspectRatio component.

type Ratio = (typeof ratios)[number];

const dimensions = ['maxWidth', 'minWidth', 'width'] as const;

type Dimension = (typeof dimensions)[number];

// This component has some StyleInterpolationProps. Those are the width properties of the DimensionProps.
type StyleInterpolationProps = Pick<DimensionProps, Dimension>;

type AspectRatioProps = StyleInterpolationProps & {
  /**
   * The box’s preferred aspect ratio is the specified ratio of width / height. When ratio is 1/1
   * it is a square.
   */
  ratio?: Ratio;
  /**
   * The child.
   */
  children: VNode<any>;
};

// Create an array [dimensionInterpolations['maxWidth'], dimensionInterpolations['width'], dimensionInterpolations['minWidth']]
const widthDimensionInterpolation = Array.from(dimensions, (x) => dimensionInterpolations[x]);
const interpolations = mergeInterpolations<StyleInterpolationProps>(widthDimensionInterpolation);

/**
 * The AspectRatio component displays its content with a certain ratio based on the dimension
 * properties. Overflow content is hidden.
 *
 * It uses a common padding-bottom hack to do this. In future versions it will
 * be implemented using the CSS's aspect-ratio property when the browsers we need to support
 * all have it. For example, Safari 15 has it, but we won't drop Safari 14 until jet 14.
 *
 */
function AspectRatio({ children, ratio = '1/1', ...props }: AspectRatioProps) {
  const { class: cls, ...styles } = interpolations(props);
  return (
    <div class={classNames([variant[ratio], cls])} style={styles}>
      {children}
    </div>
  );
}

export { ratios, AspectRatio };
